import { useEffect, useState } from "react";
import axios from "axios";
import { UNITS } from "@/data/units";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATIC_SLUGS = new Set(UNITS.map((u) => u.slug));
const src = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

/** Map a published Management Portal listing to the public unit card/detail shape. */
function toUnit(r) {
  const photos = [...(r.photos || [])].sort((a, b) => (b.is_cover ? 1 : 0) - (a.is_cover ? 1 : 0) || (a.order ?? 0) - (b.order ?? 0));
  const gallery = photos.map((p) => src(p.url)).filter(Boolean);
  const isLuxury = r.property?.type === "luxury_home";
  const isPrivateHome = r.property?.type === "furnished_private_home";
  return {
    slug: r.slug, name: r.name, fromDb: true, type: r.property?.type,
    area: [r.address || r.property?.address, r.property?.city].filter(Boolean).join(" · ") || r.property?.name || "",
    bathroom: r.tier || (isLuxury || isPrivateHome ? "Entire home" : "Private room"), bed: "",
    bedrooms: r.bedrooms ?? null, bathrooms: r.bathrooms ?? null, sqft: r.sqft ?? null, maxGuests: r.max_guests ?? null,
    short: (r.description || "").slice(0, 180), description: r.description || "",
    cover: gallery[0] || "", gallery, photos: r.photos || [],
    amenities: r.amenities?.length ? r.amenities : r.property?.amenities || [],
    houseRules: r.house_rules?.length ? r.house_rules : r.property?.rules || [],
    nearby: [], airbnbUrl: "/book", monthlyRate: r.monthly_rate,
    category: isLuxury ? "luxury" : isPrivateHome ? "private-home" : "private-room", isPremium: isLuxury,
  };
}

/** Admin-uploaded photos for the original static inventory, keyed by slug: {gallery, photos}. */
export function usePhotoOverrides() {
  const [map, setMap] = useState({});
  useEffect(() => {
    let alive = true;
    if (!process.env.REACT_APP_BACKEND_URL) return undefined;
    axios.get(`${API}/booking/listings`).then(({ data }) => {
      if (!alive) return;
      const out = {};
      (Array.isArray(data) ? data : []).forEach((r) => {
        if (STATIC_SLUGS.has(r.slug) && r.photos?.length) {
          const u = toUnit(r);
          out[r.slug] = { gallery: u.gallery, cover: u.cover, photos: r.photos };
        }
      });
      setMap(out);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return map;
}

/** Published listings added in the Management Portal that are not part of the original static inventory. */
export function usePublishedListings(type) {
  const [list, setList] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!process.env.REACT_APP_BACKEND_URL) { setList([]); return undefined; }
    axios.get(`${API}/booking/listings`, { params: type ? { type } : {} })
      .then(({ data }) => { if (alive) setList((Array.isArray(data) ? data : []).filter((r) => !STATIC_SLUGS.has(r.slug)).map(toUnit)); })
      .catch(() => { if (alive) setList([]); });
    return () => { alive = false; };
  }, [type]);
  return list;
}
